import { useState, useContext, useMemo, useEffect, useRef } from "react";
import PrivateLayout from "@/components/layout/auth/PrivateLayout";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/hooks/useI18n";
import { toast } from "@/hooks/useToast";
import { Inbox, Map, CheckCircle, Plus } from "lucide-react";

import { TravelRequestModal, ClientRequestModal } from "@/lib/requests";
import { Modal } from "@/components/ui/modal";
import { DashboardTravelRequest } from "@/lib/models/dashboard";
import { TabsHeader } from "@/components/ui/tabs-header";
import { Tabs, ProfileTabsContent } from "@/components/ui/tabs";
import { useSidebar } from "@/contexts/SidebarContext";
import { AuthContext } from "@/lib/auth/models/auth.model";
import { useDashboardData } from "@/hooks/useDashboardData";

import { DashboardRequestsTab } from "@/lib/dashboard/DashboardRequestsTab";
import { RequestsTabI18n } from "@/lib/dashboard/types";

type RequestView = "active" | "completed";

/**
 * La vista di una richiesta discende dal suo stato: `completed`/`rejected` sono
 * chiuse, tutto il resto è aperto. Da prendere in carico o già in lavorazione
 * stanno insieme: a distinguerle basta il pallino sulla card.
 */
const viewOfRequest = (request: DashboardTravelRequest): RequestView =>
  request.status === "completed" || request.status === "rejected" ? "completed" : "active";

const RequestsPage = () => {
  const { t } = useI18n();

  const {
    travelRequests,
    loading,
    error,
    refreshData,
    acceptTravelRequest,
    declineTravelRequest,
    assignTravelRequest,
  } = useDashboardData({ skipItineraries: true });

  const authContext = useContext(AuthContext);
  if (!authContext) throw new Error("RequestsPage must be used inside an AuthProvider");
  const userRole = authContext.userRole;
  const currentUserId = authContext.user?.id;

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isSidebarCollapsed } = useSidebar();
  const activeTab = searchParams.get("tab") || "active";
  const selectedRequestId = searchParams.get("requestId");

  // ── Modal state ──────────────────────────────────────────────────────────
  const [selectedTravelRequest, setSelectedTravelRequest] = useState<DashboardTravelRequest | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // L'agency ha aperto la modale per girare la richiesta a un membro: il footer
  // di assegnazione va mostrato anche se la richiesta è già assegnata.
  const [isAssignMode, setIsAssignMode] = useState(false);
  const [selectedRequestForItinerary, setSelectedRequestForItinerary] = useState<DashboardTravelRequest | null>(null);
  const [isCreateItineraryModalOpen, setIsCreateItineraryModalOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // "Nuova richiesta" sta nella testata: vale su tutte le viste, non solo sulle attive.
  const [isCreateRequestModalOpen, setIsCreateRequestModalOpen] = useState(false);

  // ── Liste per vista ──────────────────────────────────────────────────────
  const requestsByView = useMemo(() => {
    const buckets: Record<RequestView, DashboardTravelRequest[]> = {
      active: [],
      completed: [],
    };
    travelRequests.forEach((request) => buckets[viewOfRequest(request)].push(request));
    return buckets;
  }, [travelRequests]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshData();
    setRefreshing(false);
  };

  // ── Modal handlers ───────────────────────────────────────────────────────
  const handleViewRequest = (request: DashboardTravelRequest) => {
    setSelectedTravelRequest(request);
    setIsModalOpen(true);
  };

  const handleAssignRequestToMember = (request: DashboardTravelRequest) => {
    setSelectedTravelRequest(request);
    setIsAssignMode(true);
    setIsModalOpen(true);
  };

  const handleCloseRequestModal = () => {
    setIsModalOpen(false);
    setIsAssignMode(false);
    setSelectedTravelRequest(null);

    if (!selectedRequestId) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("requestId");
    setSearchParams(nextParams);
  };

  const handleCreateItinerary = (request: DashboardTravelRequest) => {
    setSelectedRequestForItinerary(request);
    setIsCreateItineraryModalOpen(true);
  };

  const handleStartBuilding = () => {
    navigate(
      selectedRequestForItinerary
        ? `/itinerary-builder?requestId=${selectedRequestForItinerary.id}`
        : "/itinerary-builder"
    );
  };

  const handleTabChange = (value: string) => setSearchParams({ tab: value });

  // ── Deep link a una travel request (?requestId=…) ─────────────────────────
  // Tipicamente si arriva da una notifica o da una mail mentre la pagina è già
  // montata: `useDashboardData` fetcha solo al mount, quindi la lista in memoria
  // è anteriore alla richiesta appena arrivata e la find() fallirebbe in
  // silenzio. Quando l'id non è in lista si rilegge una volta sola, poi si
  // dichiara forfait.
  const refreshedForRequestRef = useRef<string | null>(null);

  useEffect(() => {
    if (!selectedRequestId) {
      refreshedForRequestRef.current = null;
      return;
    }

    const request = travelRequests.find((item) => item.id === selectedRequestId);
    if (request) {
      setSelectedTravelRequest(request);
      setIsModalOpen(true);

      // Senza `tab` esplicito si atterra sulla vista che contiene davvero la
      // richiesta: aprire la modale su una lista che non la elenca confonde.
      if (!searchParams.get("tab")) {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set("tab", viewOfRequest(request));
        setSearchParams(nextParams, { replace: true });
      }
      return;
    }

    // Fetch già in corso: si rivaluta quando arrivano i dati.
    if (loading) {
      return;
    }

    if (refreshedForRequestRef.current !== selectedRequestId) {
      refreshedForRequestRef.current = selectedRequestId;
      void refreshData();
      return;
    }

    // Rilettura fatta e la richiesta continua a non esserci: eliminata, oppure
    // non visibile con i permessi correnti. Meglio dirlo che non aprire nulla.
    toast({
      title: String(t("dashboard.requestNotFoundTitle")),
      description: String(t("dashboard.requestNotFoundDesc")),
      variant: "destructive",
    });

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("requestId");
    setSearchParams(nextParams, { replace: true });
  }, [selectedRequestId, travelRequests, loading, refreshData, searchParams, setSearchParams, t]);

  // ── Config delle viste ───────────────────────────────────────────────────
  const viewConfigs: Array<{
    value: RequestView;
    labelKey: string;
    icon: typeof Inbox;
    i18n: RequestsTabI18n;
  }> = useMemo(
    () => [
      {
        value: "active",
        labelKey: "requests.tabActive",
        icon: Inbox,
        i18n: {
          countKey: "requests.activeCount",
          noCountKey: "requests.noActive",
          emptyTitleKey: "requests.noActiveTitle",
          emptyDescKey: "requests.noActiveDesc",
        },
      },
      {
        value: "completed",
        labelKey: "requests.tabCompleted",
        icon: CheckCircle,
        i18n: {
          countKey: "requests.completedCount",
          noCountKey: "requests.noCompleted",
          emptyTitleKey: "requests.noCompletedTitle",
          emptyDescKey: "requests.noCompletedDesc",
        },
      },
    ],
    []
  );

  const tabs = useMemo(
    () =>
      viewConfigs.map((cfg) => ({
        value: cfg.value,
        label: t(cfg.labelKey as any),
        icon: cfg.icon,
        badge: requestsByView[cfg.value].length.toString(),
      })),
    [viewConfigs, t, requestsByView]
  );

  return (
    <PrivateLayout
      pageTitle={t("requests.pageTitle")}
      pageDescription={t("requests.pageDescription")}
      showBackButton={false}
      headerAction={
        <Button size="lg" className="h-8 px-3" onClick={() => setIsCreateRequestModalOpen(true)}>
          <Plus className="mr-1 h-4 w-4" />
          {t("clientRequest.newRequest")}
        </Button>
      }
      headerActionCompact={
        <Button
          size="sm"
          className="h-8 flex-shrink-0 whitespace-nowrap rounded-full px-2.5 text-xs"
          onClick={() => setIsCreateRequestModalOpen(true)}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          {t("clientRequest.newRequest")}
        </Button>
      }
    >
      <div className="space-y-6">
        <TabsHeader
          activeTab={activeTab}
          onTabChange={handleTabChange}
          tabs={tabs}
          sidebarCollapsed={isSidebarCollapsed}
        />

        <div className="xl:px-8 md:px-4 mx-auto">
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            {viewConfigs.map((cfg) => (
              <ProfileTabsContent key={cfg.value} value={cfg.value}>
                <DashboardRequestsTab
                  userRole={userRole}
                  travelRequests={requestsByView[cfg.value]}
                  loading={loading}
                  error={error}
                  onRefresh={handleRefresh}
                  refreshing={refreshing}
                  onViewRequest={handleViewRequest}
                  onCreateItinerary={handleCreateItinerary}
                  onAssignToMember={
                    userRole === "agency" || userRole === "admin"
                      ? handleAssignRequestToMember
                      : undefined
                  }
                  i18n={cfg.i18n}
                />
              </ProfileTabsContent>
            ))}
          </Tabs>
        </div>
      </div>

      {/* Nuova richiesta cliente: stessa modale usata dal tab in modifica */}
      <ClientRequestModal
        isOpen={isCreateRequestModalOpen}
        onClose={() => setIsCreateRequestModalOpen(false)}
        onSuccess={handleRefresh}
      />

      {/* Travel request detail modal */}
      <TravelRequestModal
        isOpen={isModalOpen}
        onClose={handleCloseRequestModal}
        travelRequest={selectedTravelRequest}
        onAccept={async (id: string) => await acceptTravelRequest(id)}
        onDecline={async (id: string) => await declineTravelRequest(id)}
        onAssign={async (id: string, designerId: string) =>
          await assignTravelRequest(id, designerId, userRole === "agency" ? "pending" : "assigned")
        }
        isAdmin={userRole === "admin"}
        agencyId={userRole === "agency" ? currentUserId : undefined}
        enableDetailSheets
        showActions={
          (userRole === "admin" &&
            (isAssignMode || selectedTravelRequest?.status === "pending")) ||
          (userRole === "agency" &&
            (isAssignMode || selectedTravelRequest?.status === "pending")) ||
          ((userRole === "travel_designer" ||
            userRole === "travel_advisor_member") &&
            selectedTravelRequest?.status === "pending" &&
            !selectedTravelRequest?.isExpired)
        }
      />

      {/* Create itinerary confirmation modal */}
      <Modal
        isOpen={isCreateItineraryModalOpen}
        onClose={() => {
          setIsCreateItineraryModalOpen(false);
          setSelectedRequestForItinerary(null);
        }}
        title=""
        size="sm"
        showHeader={false}
        footer={
          <div className="flex items-center justify-between w-full">
            <Button
              variant="ghost"
              onClick={() => {
                setIsCreateItineraryModalOpen(false);
                setSelectedRequestForItinerary(null);
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button onClick={handleStartBuilding} className="flex items-center gap-2">
              {t("dashboard.startBuilding")}
            </Button>
          </div>
        }
      >
        <div className="text-center py-20">
          <div className="flex justify-center mb-4">
            <Map className="h-8 w-8" />
          </div>
          <h2 className="text-gray-900 mb-2">{t("dashboard.readyToCreate")}</h2>
          <p className="text-gray-600">{t("dashboard.eachItineraryIndependent")}</p>
        </div>
      </Modal>
    </PrivateLayout>
  );
};

export default RequestsPage;
